import { lazy } from 'react';
import { RouteObject } from 'react-router-dom';

const HomePage = lazy(() => import('../pages/home/page'));
const DiscoverPage = lazy(() => import('../pages/discover/page'));
const EventsPage = lazy(() => import('../pages/events/page'));
const EventDetailPage = lazy(() => import('../pages/event-detail/page'));
const EventRegisterPage = lazy(() => import('../pages/event-register/page'));
const CreateEventPage = lazy(() => import('../pages/create-event/page'));
const EditEventPage = lazy(() => import('../pages/edit-event/page'));
const SubscribePage = lazy(() => import('../pages/subscribe/page'));
const LoginPage = lazy(() => import('../pages/login/page'));
const SignupPage = lazy(() => import('../pages/signup/page'));
const ForgotPasswordPage = lazy(() => import('../pages/forgot-password/page'));
const ResetPasswordPage = lazy(() => import('../pages/reset-password/page'));
const AdminLoginPage = lazy(() => import('../pages/admin-login/page'));
const AdminLayout = lazy(() => import('../layouts/AdminLayout'));
const AdminPage = lazy(() => import('../pages/admin/page'));
const ManageEventsPage = lazy(() => import('../pages/admin/manage-events/page'));
const ManageUsersPage = lazy(() => import('../pages/admin/manage-users/page'));
const AdminProfilePage = lazy(() => import('../pages/admin/profile/page'));
const SettingsPage = lazy(() => import('../pages/admin/settings/page'));
const EmailPromotionsPage = lazy(() => import('../pages/admin/email-promotions/page'));
const AdminWithdrawalsPage = lazy(() => import('../pages/admin/withdrawals/page'));
const UserLayout = lazy(() => import('../layouts/UserLayout'));
const UserDashboardPage = lazy(() => import('../pages/user-dashboard/page'));
const MyEventsPage = lazy(() => import('../pages/user-dashboard/my-events/page'));
const MyRegistrationsPage = lazy(() => import('../pages/user-dashboard/my-registrations/page'));
const EventRegistrationsPage = lazy(() => import('../pages/user-dashboard/event-registrations/page'));
const ProfilePage = lazy(() => import('../pages/user-dashboard/profile/page'));
const MyStorePage = lazy(() => import('../pages/user-dashboard/my-store/page'));
const StoreOrdersPage = lazy(() => import('../pages/user-dashboard/my-store/orders/page'));
const EarningsPage = lazy(() => import('../pages/user-dashboard/earnings/page'));
const SubscriptionManagePage = lazy(() => import('../pages/user-dashboard/subscription/page'));
const TeamPage = lazy(() => import('../pages/user-dashboard/team/page'));
const StorePage = lazy(() => import('../pages/store/page'));
const PaymentSuccessPage = lazy(() => import('../pages/payment-success/page'));
const NotFoundPage = lazy(() => import('../pages/NotFound'));
const ConfirmEmailPage = lazy(() => import('../pages/confirm-email/page'));
const RefundPolicyPage = lazy(() => import('../pages/legal/refund-policy/page'));
const FreeEventsPage = lazy(() => import('../pages/free-events/page'));
const ThisWeekendPage = lazy(() => import('../pages/this-weekend/page'));
const CategoriesPage = lazy(() => import('../pages/categories/page'));
const HowItWorksPage = lazy(() => import('../pages/how-it-works/page'));
const CreatorGuidePage = lazy(() => import('../pages/creator-guide/page'));
const SuccessStoriesPage = lazy(() => import('../pages/success-stories/page'));
const ResourcesPage = lazy(() => import('../pages/resources/page'));
const PrivacyPolicyPage = lazy(() => import('../pages/legal/privacy-policy/page'));
const TermsPage = lazy(() => import('../pages/legal/terms/page'));
const CookiePolicyPage = lazy(() => import('../pages/legal/cookies/page'));

const routes: RouteObject[] = [
  {
    path: '/',
    element: <HomePage />,
  },
  {
    path: '/discover',
    element: <DiscoverPage />,
  },
  {
    path: '/events',
    element: <EventsPage />,
  },
  {
    path: '/event/:id',
    element: <EventDetailPage />,
  },
  {
    path: '/event/:id/register',
    element: <EventRegisterPage />,
  },
  {
    path: '/create-event',
    element: <CreateEventPage />,
  },
  {
    path: '/edit-event/:id',
    element: <EditEventPage />,
  },
  {
    path: '/create',
    element: <SubscribePage />,
  },
  {
    path: '/login',
    element: <LoginPage />,
  },
  {
    path: '/signup',
    element: <SignupPage />,
  },
  {
    path: '/forgot-password',
    element: <ForgotPasswordPage />,
  },
  {
    path: '/reset-password',
    element: <ResetPasswordPage />,
  },
  {
    path: '/admin-login',
    element: <AdminLoginPage />,
  },
  {
    path: '/admin',
    element: <AdminLayout />,
    children: [
      {
        index: true,
        element: <AdminPage />,
      },
      {
        path: 'manage-events',
        element: <ManageEventsPage />,
      },
      {
        path: 'manage-users',
        element: <ManageUsersPage />,
      },
      {
        path: 'profile',
        element: <AdminProfilePage />,
      },
      {
        path: 'settings',
        element: <SettingsPage />,
      },
      {
        path: 'email-promotions',
        element: <EmailPromotionsPage />,
      },
      {
        path: 'withdrawals',
        element: <AdminWithdrawalsPage />,
      },
    ],
  },
  {
    path: '/dashboard',
    element: <UserLayout />,
    children: [
      {
        index: true,
        element: <UserDashboardPage />,
      },
      {
        path: 'my-events',
        element: <MyEventsPage />,
      },
      {
        path: 'my-registrations',
        element: <MyRegistrationsPage />,
      },
      {
        path: 'event-registrations/:eventId',
        element: <EventRegistrationsPage />,
      },
      {
        path: 'profile',
        element: <ProfilePage />,
      },
      {
        path: 'my-store',
        element: <MyStorePage />,
      },
      {
        path: 'my-store/orders',
        element: <StoreOrdersPage />,
      },
      {
        path: 'earnings',
        element: <EarningsPage />,
      },
      {
        path: 'subscription',
        element: <SubscriptionManagePage />,
      },
      {
        path: 'team',
        element: <TeamPage />,
      },
    ],
  },
  {
    path: '/store/:userId',
    element: <StorePage />,
  },
  {
    path: '/store',
    element: <StorePage />,
  },
  {
    path: '/payment-success',
    element: <PaymentSuccessPage />,
  },
  {
    path: '/confirm-email',
    element: <ConfirmEmailPage />,
  },
  {
    path: '/legal/refund-policy',
    element: <RefundPolicyPage />,
  },
  {
    path: '/events/free',
    element: <FreeEventsPage />,
  },
  {
    path: '/events/this-weekend',
    element: <ThisWeekendPage />,
  },
  {
    path: '/categories',
    element: <CategoriesPage />,
  },
  {
    path: '/how-it-works',
    element: <HowItWorksPage />,
  },
  {
    path: '/creator-guide',
    element: <CreatorGuidePage />,
  },
  {
    path: '/success-stories',
    element: <SuccessStoriesPage />,
  },
  {
    path: '/resources',
    element: <ResourcesPage />,
  },
  {
    path: '/legal/privacy-policy',
    element: <PrivacyPolicyPage />,
  },
  {
    path: '/legal/terms',
    element: <TermsPage />,
  },
  {
    path: '/legal/cookies',
    element: <CookiePolicyPage />,
  },
  {
    path: '*',
    element: <NotFoundPage />,
  },
];

export default routes;